import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Container, Form, Pagination, Row, Table,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  assignShiftAllotment,
  getAllShiftAllotments,
  getAllShifts,
  getAllDepartments,
  getAllEmployees,
} from "../../../../api/onboarddeboard";

const ShiftAllotment = () => {
  const token = localStorage.getItem("token");

  const [shiftId, setShiftId] = useState("");
  const [userId, setUserId] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [departmentId, setDepartmentId] = useState("");
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [hoveredEmployees, setHoveredEmployees] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [criteria, setCriteria] = useState("ALL");
  const [employeeCurrentPage, setEmployeeCurrentPage] = useState(1);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  const hoverTimeout = useRef(null);
  const EMPLOYEE_PER_PAGE = 20;

  const fetchData = useCallback(async () => {
    try {
      setEmployeeLoading(true);
      const [shiftRes, deptRes, employeeRes] = await Promise.all([
        getAllShifts(token),
        getAllDepartments(token),
        getAllEmployees(token),
      ]);

      setShifts((shiftRes.data || []).filter((s) => s.isActive));
     setDepartments(deptRes.data || []);
      setAllUsers((employeeRes.data || []).filter((u) => u.deboardEmp !== true));

      setLoading(true);
      const tableRes = await getAllShiftAllotments(token);
      console.log("tables", tableRes.data);
      setTableData(tableRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setEmployeeLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const getWorkingDays = (shift) => {
    if (!shift) return 0;
    const days = [
      shift.monWeekOff, shift.tueWeekOff, shift.wedWeekOff,
      shift.thuWeekOff, shift.friWeekOff, shift.satWeekOff, shift.sunWeekOff,
    ];
    return days.filter((d) => d === false).length;
  };

  const groupedData = Object.values(
    (Array.isArray(tableData) ? tableData : []).reduce((acc, item) => {
      const sId = item.shift?.id;
      if (!acc[sId]) {
        acc[sId] = { shift: item.shift, employees: [] };
      }
      acc[sId].employees.push(item.user?.name || "");
      return acc;
    }, {})
  );

 const validateForm = () => {
    let newErrors = {};
    if (!shiftId) newErrors.shiftId = "Shift is required";
    if (criteria === "DEPT" && !departmentId) newErrors.departmentId = "Department is required";
    
    if (selectableUsers.length === 0) {
      newErrors.userId = "All employees are already allotted";
    } else if (userId.length === 0) {
      newErrors.userId = "Employee is required";
      toast.error("Please select at least one employee");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await Promise.all(userId.map((id) => assignShiftAllotment(id, shiftId, token)));
      toast.success("Shift is Allotted Successfully");
      setShiftId("");
      setUserId([]);
      setDepartmentId("");
      setErrors({});
      fetchData();
    } catch (err) {
      toast.error("Error Allotting shift");
    }
  };

  const ITEMS_PER_PAGE = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const processedData = groupedData
    .filter((item) => item.shift?.shiftName?.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const nameA = a.shift?.shiftName?.toLowerCase() || "";
      const nameB = b.shift?.shiftName?.toLowerCase() || "";
      if (sortOrder === "asc") return nameA.localeCompare(nameB);
      return nameB.localeCompare(nameA);
    });

  const totalPages = Math.ceil(processedData.length / ITEMS_PER_PAGE);
  const paginatedData = processedData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const filteredUsers = criteria === "DEPT"
    ? allUsers.filter((u) => String(u.department?.id ?? u.departmentId) === String(departmentId))
    : allUsers;

  const handleCancel = () => {
    setShiftId("");
    setUserId([]);
    setDepartmentId("");
    setErrors({});
  };

  const employeeTotalPages = Math.ceil(filteredUsers.length / EMPLOYEE_PER_PAGE);
  const paginatedEmployees = [...filteredUsers].slice(
    (employeeCurrentPage - 1) * EMPLOYEE_PER_PAGE,
    employeeCurrentPage * EMPLOYEE_PER_PAGE
  );

  const MAX_VISIBLE = 20;
  const visibleEmployees = hoveredEmployees ? hoveredEmployees.slice(0, MAX_VISIBLE) : [];

  const exportToCSV = () => {
    if (!processedData.length) {
      toast.error("No data to export");
      return;
    }
    const headers = ["Sr No", "Shift Name", "Working Days", "Employee Count"];
    const rows = processedData.map((item, index) => [
      index + 1,
      item.shift?.shiftName || "-",
      getWorkingDays(item.shift),
      item.employees.length,
    ]);
    let csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].map((row) => row.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "shift_allotment.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const assignedUsersMap = (Array.isArray(tableData) ? tableData : []).reduce((acc, item) => {
if (item.user?.id) acc[item.user.id] = item.shift?.shiftName;
    return acc;
  }, {});

  const selectableUsers = filteredUsers.filter((u) => !assignedUsersMap[u.id]);

  const handleEmployeeCellMouseEnter = (e, employees) => {
    clearTimeout(hoverTimeout.current);
    const rect = e.target.getBoundingClientRect();
    setHoveredEmployees(employees);
    setHoverPosition({ x: rect.left - 120, y: rect.bottom });
  };

  const handleEmployeeCellMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setHoveredEmployees(null), 200);
  };

  const renderShiftTableBody = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="4" className="text-center py-4">
            <div className="d-flex justify-content-center align-items-center">
              <div className="spinner-border text-primary"><span className="visually-hidden">Loading...</span></div>
              <span style={{ marginLeft: "10px" }}>Loading...</span>
            </div>
          </td>
        </tr>
      );
    }

    if (paginatedData.length > 0) {
      return paginatedData.map((item, index) => (
        <tr key={item.shift?.id}>
          <td style={{ textAlign: "center", height: "35px", padding: "2px", verticalAlign: "middle" }}>
            {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
          </td>
          <td style={{ textAlign: "center", height: "35px", padding: "2px", verticalAlign: "middle" }}>
            {item.shift?.shiftName || "-"}
          </td>
          <td style={{ textAlign: "center", height: "35px", padding: "2px", verticalAlign: "middle" }}>
            {getWorkingDays(item.shift)}
          </td>
          <td
            style={{ textAlign: "center", cursor: "pointer", height: "35px", padding: "2px", verticalAlign: "middle" }}
            onMouseEnter={(e) => handleEmployeeCellMouseEnter(e, item.employees)}
            onMouseLeave={handleEmployeeCellMouseLeave}
          >
            {item.employees.length}
          </td>
        </tr>
      ));
    }

    return (<tr><td colSpan="4" className="text-center">No Data Available</td></tr>);
  };

  const handleCheckboxChange = (e, userId_param, isAssigned) => {
    if (isAssigned) return;
    if (e.target.checked) {
      setUserId((prev) => [...prev, userId_param]);
    } else {
      setUserId((prev) => prev.filter((id) => id !== userId_param));
    }
    setErrors((prev) => ({ ...prev, userId: "" }));
  };

  const renderEmployeeTableBody = () => {
    if (employeeLoading) {
      return (
        <tr>
          <td colSpan="3" className="text-center py-4">
            <div className="d-flex justify-content-center align-items-center">
              <div className="spinner-border text-primary"><span className="visually-hidden">Loading...</span></div>
              <span style={{ marginLeft: "10px" }}>Loading Employees...</span>
            </div>
          </td>
        </tr>
      );
    }

    if (paginatedEmployees.length > 0) {
      return paginatedEmployees.map((u, index) => {
        const assignedShift = assignedUsersMap[u.userInfoId];
        const isAssigned = !!assignedShift;

        return (
          <tr key={u.userInfoId}>
            <td style={{ textAlign: "center", padding: "4px", height: "35px", verticalAlign: "middle" }}>
              <Form.Check
                type="checkbox"
                disabled={isAssigned}
                checked={isAssigned || userId.includes(u.userInfoId)}
                onChange={(e) => handleCheckboxChange(e, u.userInfoId, isAssigned)}
              />
            </td>
            <td style={{ textAlign: "center", padding: "4px", height: "35px", verticalAlign: "middle" }}>
              {(employeeCurrentPage - 1) * EMPLOYEE_PER_PAGE + index + 1}
            </td>
            <td style={{ padding: "4px", height: "35px", textAlign: "center", verticalAlign: "middle" }}>
              {u.name}
              {assignedUsersMap[u.userInfoId] && (
                <span style={{ color: "#666", fontSize: "13px", marginLeft: "5px", fontWeight: "500" }}>
                  ({assignedUsersMap[u.userInfoId]})
                </span>
              )}
            </td>
          </tr>
        );
      });
    }

    return (<tr><td colSpan="3" className="text-center text-muted">No Employees Found</td></tr>);
  };

  const handleTooltipMouseEnter = () => clearTimeout(hoverTimeout.current);
  const handleTooltipMouseLeave = () => {
    hoverTimeout.current = setTimeout(() => setHoveredEmployees(null), 150);
  };

  return (
    <Container fluid className="p-4">
      <style>{`
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
      `}</style>
      <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Shift Allotment</h4>
      <ToastContainer position="top-right" autoClose={3000} />
      <Breadcrumb className="lc-breadcrumb">
        <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
        <Breadcrumb.Item href="#">Shift Management</Breadcrumb.Item>
        <Breadcrumb.Item active>Shift Allotment</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6} className="mt-2">
                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Shift <span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select value={shiftId} onChange={(e) => { setShiftId(e.target.value); setErrors((prev) => ({ ...prev, shiftId: "" })); }}>
                          <option value="">Select Shift</option>
                          {shifts.map((s) => (<option key={s.id} value={s.id}>{s.shiftName}</option>))}
                        </Form.Select>
                        {errors.shiftId && <div style={{ color: "red", fontSize: "13px" }}>{errors.shiftId}</div>}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Criteria</Form.Label>
                      <Col sm={8}>
                        <Form.Select
                          value={criteria}
                          onChange={(e) => { setCriteria(e.target.value); setDepartmentId(""); setUserId([]); setEmployeeCurrentPage(1); }}
                        >
                          <option value="ALL">All Employees</option>
                          <option value="DEPT">Department Wise</option>
                        </Form.Select>
                      </Col>
                    </Row>

                    {criteria === "DEPT" && (
                      <Row className="mb-3">
                        <Form.Label className="col-sm-4 col-form-label form-label">Department <span className="text-danger">*</span></Form.Label>
                        <Col sm={8}>
                          <Form.Select
                            value={departmentId}
                            onChange={(e) => { setDepartmentId(e.target.value); setEmployeeCurrentPage(1); setErrors((prev) => ({ ...prev, departmentId: "" })); setUserId([]); }}
                          >
                            <option value="">Select Department</option>
                            {departments.map((d) => (<option key={d.id} value={d.id}>{d.departmentName}</option>))}
                          </Form.Select>
                          {errors.departmentId && <div style={{ color: "red", fontSize: "13px" }}>{errors.departmentId}</div>}
                        </Col>
                      </Row>
                    )}
                  </Col>

                  <Col md={6} className="mt-2">
                    <Row className="justify-content-end">
                      <Col lg={5} md={12} className="mb-2">
                        <div style={{ position: "relative" }}>
                          <Form.Control
                            type="text"
                            placeholder="Search Shift..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            style={{ paddingRight: "35px" }}
                          />
                          {searchTerm && (
                            <i
                              className="bi bi-x-circle"
                              onClick={() => { setSearchTerm(""); setCurrentPage(1); }}
                              style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: "#999", fontSize: "16px" }}
                            />
                          )}
                        </div>
                      </Col>

                      <Col lg={4} md={6} className="mb-2">
                        <Form.Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                          <option value="asc">Sort A-Z</option>
                          <option value="desc">Sort Z-A</option>
                        </Form.Select>
                      </Col>

                      <Col xs="auto" className="d-flex justify-content-end mb-2" md="auto">
                        <i
                          className="bi bi-download rounded"
                          title="Export CSV"
                          style={{ cursor: "pointer", color: "#2E5CB8", border: "1px solid #2E5CB8", padding: "7px", paddingLeft: "10px", paddingRight: "10px" }}
                          onClick={exportToCSV}
                        />
                      </Col>
                    </Row>

                    <Table bordered hover responsive>
                      <thead>
                        <tr>
                          <th style={{ backgroundColor: "#2E5CB8", color: "white", height: "35px", padding: "2px", textAlign: "center", width: "10%", verticalAlign: "middle" }}>Sr. No.</th>
                          <th style={{ backgroundColor: "#2E5CB8", color: "white", height: "35px", padding: "2px", textAlign: "center", width: "40%", verticalAlign: "middle" }}>Shift Name</th>
                          <th style={{ backgroundColor: "#2E5CB8", color: "white", height: "35px", padding: "2px", textAlign: "center", width: "25%", verticalAlign: "middle" }}>Working Days</th>
                          <th style={{ backgroundColor: "#2E5CB8", color: "white", height: "35px", padding: "2px", textAlign: "center", width: "25%", verticalAlign: "middle" }}>Employee Count</th>
                        </tr>
                      </thead>
                      <tbody>{renderShiftTableBody()}</tbody>
                    </Table>

                    {hoveredEmployees && (
                      <div
                        onMouseEnter={handleTooltipMouseEnter}
                        onMouseLeave={handleTooltipMouseLeave}
                        style={{
                          position: "fixed", top: hoverPosition.y + 5, left: hoverPosition.x,
                          background: "#fff", border: "1px solid #ddd", borderRadius: "10px", padding: "12px",
                          boxShadow: "0 6px 15px rgba(0,0,0,0.15)", zIndex: 1000, maxHeight: "250px",
                          overflowY: "auto", minWidth: "220px", userSelect: "text",
                        }}
                      >
                        <div style={{ position: "sticky", top: 0, background: "#fff", fontWeight: "600", borderBottom: "1px solid #eee", paddingBottom: "4px", zIndex: 1 }}>
                          Employees ({hoveredEmployees.length})
                        </div>
                        <ul>{visibleEmployees.map((emp, i) => (<li key={i}>{emp}</li>))}</ul>
                        {hoveredEmployees.length > MAX_VISIBLE && (
                          <div style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
                            +{hoveredEmployees.length - MAX_VISIBLE} more...
                          </div>
                        )}
                      </div>
                    )}

                    <div className="d-flex justify-content-end">
                      <Pagination size="sm">
                        <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                        {[...Array(totalPages).keys()].map((number) => (
                          <Pagination.Item key={number + 1} active={number + 1 === currentPage} onClick={() => setCurrentPage(number + 1)}>{number + 1}</Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                      </Pagination>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col className="d-flex justify-content-center">
                    <Button type="submit" style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}>Submit</Button>
                    <Button variant="secondary" className="ms-2" onClick={handleCancel}>Cancel</Button>
                  </Col>
                </Row>
              </Form>

              <hr className="my-4" />

              <Row>
                <div>
                  <Table bordered hover responsive className="mb-0">
                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ width: "10%", height: "35px", textAlign: "center", backgroundColor: "#2E5CB8", color: "white", padding: "4px", verticalAlign: "middle" }}>
                          <Form.Check
                            type="checkbox"
                            checked={
                              paginatedEmployees.length > 0 &&
                              paginatedEmployees.every((u) => assignedUsersMap[u.userInfoId] || userId.includes(u.userInfoId))
                            }
                            onChange={(e) => {
                              const currentPageSelectableUsers = paginatedEmployees.filter((u) => !assignedUsersMap[u.userInfoId]);
                              if (e.target.checked) {
                                setUserId((prev) => [...new Set([...prev, ...currentPageSelectableUsers.map((u) => u.userInfoId)])]);
                              } else {
                                setUserId((prev) => prev.filter((id) => !currentPageSelectableUsers.some((u) => u.userInfoId === id)));
                              }
                            }}
                          />
                        </th>
                        <th style={{ width: "15%", height: "35px", textAlign: "center", backgroundColor: "#2E5CB8", color: "white", padding: "4px", verticalAlign: "middle" }}>Sr No</th>
                        <th style={{ backgroundColor: "#2E5CB8", height: "35px", color: "white", textAlign: "center", padding: "4px", verticalAlign: "middle" }}>Employee Name</th>
                      </tr>
                    </thead>
                    <tbody>{renderEmployeeTableBody()}</tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div>
                    {errors.userId && <div style={{ color: "red", fontSize: "13px", fontWeight: "500" }}>{errors.userId}</div>}
                  </div>
                  <Pagination size="sm">
                    <Pagination.Prev disabled={employeeCurrentPage === 1} onClick={() => setEmployeeCurrentPage((prev) => Math.max(prev - 1, 1))} />
                    {[...Array(employeeTotalPages).keys()].map((number) => (
                      <Pagination.Item key={number + 1} active={employeeCurrentPage === number + 1} onClick={() => setEmployeeCurrentPage(number + 1)}>{number + 1}</Pagination.Item>
                    ))}
                    <Pagination.Next disabled={employeeCurrentPage === employeeTotalPages} onClick={() => setEmployeeCurrentPage((prev) => Math.min(prev + 1, employeeTotalPages))} />
                  </Pagination>
                </div>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ShiftAllotment;
